import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import {
  TrendingUp, Target, Shield, Settings, Monitor,
  GraduationCap, Leaf, Search, ShoppingCart, Building2, X,
} from "lucide-react";

const services = [
  { icon: TrendingUp, title: "Economic Development", description: "Driving prosperity in Indigenous communities through strategic investment, enterprise development and sustainable economic pathways." },
  { icon: Target, title: "Strategy", description: "Designing long-term strategies grounded in Indigenous knowledge systems that navigate complexity with cultural authority." },
  { icon: Shield, title: "Governance", description: "Strengthening governance frameworks for iwi, trusts, and community organisations with culturally grounded oversight." },
  { icon: Settings, title: "System Design", description: "Architecting systems and processes that centre Indigenous communities, ensuring equitable and effective delivery." },
  { icon: Monitor, title: "Digital Tools", description: "Building digital solutions and platforms that empower communities, streamline operations and create lasting value." },
  { icon: GraduationCap, title: "Training", description: "Developing capability through wānanga, workshops and training programmes that uplift people and organisations." },
  { icon: Leaf, title: "Climate Adaptation", description: "Applying Indigenous foresight and science to climate resilience, preparing communities for environmental change." },
  { icon: Search, title: "Research", description: "Conducting rigorous Indigenous research that informs policy, practice and decision-making with cultural integrity." },
  { icon: ShoppingCart, title: "Procurement", description: "Delivering procurement strategies and processes that maximise value for Indigenous communities and keep economic benefit local." },
  { icon: Building2, title: "Infrastructure", description: "Planning and delivering infrastructure projects that serve communities, from papakāinga housing to civic and cultural facilities." },
];

export default function ServicesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [expanded, setExpanded] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-6 sm:py-8 overflow-hidden"
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#10B981] mb-4">
            What We Do
          </p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
            Indigenous-led <span className="text-white/95">solutions.</span>
          </h2>
        </motion.div>

        {/* 2 rows x 5 columns grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1">
          {services.map((service, i) => {
            const Icon = service.icon;
            const isOpen = expanded === i;
            const isOdd = i % 2 === 1;
            return (
              <motion.div
                key={service.title}
                data-testid={`service-box-${service.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.05 * i }}
                onClick={() => setExpanded(isOpen ? null : i)}
                className={`relative cursor-pointer transition-all duration-300 overflow-hidden border ${
                  isOpen
                    ? "bg-black/40 backdrop-blur-sm border-white/30 text-white"
                    : isOdd
                      ? "border-transparent text-white hover:opacity-90"
                      : "border-transparent text-white hover:opacity-90"
                }`}
                style={!isOpen ? { background: isOdd ? "linear-gradient(135deg, #364225 0%, #0A7A5C 50%, #3D9B7A 100%)" : "linear-gradient(135deg, #bb321a 0%, #485e23 50%, #364225 100%)" } : undefined}
              >
                <div className="p-3 sm:p-5 lg:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white/95" strokeWidth={1.5} />
                    {isOpen && <X className="w-3 h-3 sm:w-4 sm:h-4 text-white/50" />}
                  </div>
                  <h4 className="font-['Syne'] text-xs sm:text-sm lg:text-base font-bold leading-tight">
                    {service.title}
                  </h4>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="text-white/90 text-xs sm:text-sm leading-relaxed mt-3"
                      >
                        {service.description}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
